import parser from './channel-excel-parser';
import { ExcelParsedProperties, ExcelParsedProperties as props } from '../models/channel-config';
import { ErrorMessageProperties as msgprops } from '../models/channel-config';
import { ChannelType, ChannelTypeStrings } from '../models/channel-type';
import { CHANNEL_LENGTH_VALIDATION, CHANNEL_RANGE_VALIDATION, CHANNEL_VALIDATION } from '../models/channel-validate';

export class ChannelExcelValidator {
    constructor() {}

    private static readonly HARDWARE_TYPES = new Set([
        ChannelTypeStrings[ChannelType.ANALOG_420MA_AI],
        ChannelTypeStrings[ChannelType.ANALOG_420MA_PT],
        ChannelTypeStrings[ChannelType.ANALOG_1_5_V],
        ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE],
        ChannelTypeStrings[ChannelType.ANALOG_PT100_2WIRE],
        ChannelTypeStrings[ChannelType.ANALOG_TEMP_K],
        ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM],
        ChannelTypeStrings[ChannelType.ANALOG_LOGIC],
        ChannelTypeStrings[ChannelType.DIGITAL_CONTACT],
        ChannelTypeStrings[ChannelType.DIGITAL_MOTOR],
        ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM],
        ChannelTypeStrings[ChannelType.DIGITAL_LOGIC],
        ChannelTypeStrings[ChannelType.FLOW_DAY],
        ChannelTypeStrings[ChannelType.FLOW_TOTAL],
        ChannelTypeStrings[ChannelType.FLOW_EXT_COMM],
        ChannelTypeStrings[ChannelType.FLOW_LOGIC],
    ]);

    private static readonly RUNNING_TYPES = new Set([
        ChannelTypeStrings[ChannelType.RUNNING_DAY],
        ChannelTypeStrings[ChannelType.RUNNING_TOTAL],
        ChannelTypeStrings[ChannelType.RUNNING_LOGIC],
    ]);

    private static readonly DIGITAL_TYPES = new Set([
        ChannelTypeStrings[ChannelType.DIGITAL_CONTACT],
        ChannelTypeStrings[ChannelType.DIGITAL_MOTOR],
        ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM],
        ChannelTypeStrings[ChannelType.DIGITAL_LOGIC],

        ChannelTypeStrings[ChannelType.FLOW_DAY],
        ChannelTypeStrings[ChannelType.FLOW_TOTAL],
        ChannelTypeStrings[ChannelType.FLOW_EXT_COMM],
        ChannelTypeStrings[ChannelType.FLOW_LOGIC],
    ]);

    private static readonly ANALOG_TYPES = new Set([
        ChannelTypeStrings[ChannelType.ANALOG_420MA_AI],
        ChannelTypeStrings[ChannelType.ANALOG_420MA_PT],
        ChannelTypeStrings[ChannelType.ANALOG_1_5_V],
        ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE],
        ChannelTypeStrings[ChannelType.ANALOG_PT100_2WIRE],
        ChannelTypeStrings[ChannelType.ANALOG_TEMP_K],
        ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM],
        ChannelTypeStrings[ChannelType.ANALOG_LOGIC],
        ChannelTypeStrings[ChannelType.EXH_GAS_DEV],
    ]);

    private static isValuePresent(val: any): boolean {
        return val !== undefined && val !== null && val !== '';
    }

    private static parseNumber(value: any): number | null {
        if (!this.isValuePresent(value)) return null;

        const n = Number(value);
        return isNaN(n) ? null : n;
    }

    static validateExcelData(currentData: any[][]) {
        const errors = [];
        let excelProperties: any[] = Object.values(props);
        for (let i = 0; i < currentData.length; i++) {
            let channelExcelObj = {};
            if (!parser.allValuesEmpty(currentData[i])) {
                for (let j = 0; j < excelProperties.length; j++) {
                    if (excelProperties[j] !== 'scale') {
                        channelExcelObj[excelProperties[j]] = currentData[i][j];
                    }
                }
            }
            if (channelExcelObj[ExcelParsedProperties.ChannelType] === ChannelTypeStrings[ChannelType.BLANK]) {
                this.validateRequiredFieldSet(channelExcelObj, props.ChannelType, errors);
                continue;
            }

            this.validateCommonFields(channelExcelObj, errors);
            switch (channelExcelObj[ExcelParsedProperties.ChannelType]) {
                case ChannelTypeStrings[ChannelType.SYSTEM]:
                    this.validateSystem(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.RUNNING_DAY]:
                case ChannelTypeStrings[ChannelType.RUNNING_TOTAL]:
                case ChannelTypeStrings[ChannelType.RUNNING_LOGIC]:
                    this.validateRunning(channelExcelObj, errors);

                    break;
                case ChannelTypeStrings[ChannelType.FLOW_DAY]:
                case ChannelTypeStrings[ChannelType.FLOW_TOTAL]:
                case ChannelTypeStrings[ChannelType.FLOW_EXT_COMM]:
                case ChannelTypeStrings[ChannelType.FLOW_LOGIC]:
                    this.validateFlowTotal(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.EXH_GAS_DEV]:
                case ChannelTypeStrings[ChannelType.EXH_GAS_MEAN]:
                    this.validateExhGas(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]:
                    this.validateDigitalMotor(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.DIGITAL_CONTACT]:
                case ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM]:
                case ChannelTypeStrings[ChannelType.DIGITAL_LOGIC]:
                    this.validateDigitalContact(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.ANALOG_1_5_V]:
                case ChannelTypeStrings[ChannelType.ANALOG_420MA_AI]:
                case ChannelTypeStrings[ChannelType.ANALOG_420MA_PT]:
                case ChannelTypeStrings[ChannelType.ANALOG_PT100_2WIRE]:
                case ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE]:
                case ChannelTypeStrings[ChannelType.ANALOG_TEMP_K]:
                    this.validateAnalog(channelExcelObj, errors);
                    break;
                case ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM]:
                case ChannelTypeStrings[ChannelType.ANALOG_LOGIC]:
                    this.validateAnalogWithoutSensor(channelExcelObj, errors);
                    break;
            }
        }

        return errors;
    }

    static validateSingleRow(rowData: any[], rowIndex: number) {
        const errors = [];
        const excelProperties: any[] = Object.values(props);
        const channelExcelObj: Record<string, any> = {};

        if (!rowData || parser.allValuesEmpty(rowData)) {
            return errors;
        }

        for (let j = 0; j < excelProperties.length; j++) {
            const propKey = excelProperties[j];
            if (propKey !== 'scale') {
                channelExcelObj[propKey] = rowData[j];
            }
        }

        if (channelExcelObj[ExcelParsedProperties.ChannelType] === ChannelTypeStrings[ChannelType.BLANK]) {
            return errors;
        }

        this.validateCommonFields(channelExcelObj, errors);

        switch (channelExcelObj[ExcelParsedProperties.ChannelType]) {
            case ChannelTypeStrings[ChannelType.SYSTEM]:
                this.validateSystem(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.RUNNING_DAY]:
            case ChannelTypeStrings[ChannelType.RUNNING_TOTAL]:
            case ChannelTypeStrings[ChannelType.RUNNING_LOGIC]:
                this.validateRunning(channelExcelObj, errors);

                break;
            case ChannelTypeStrings[ChannelType.FLOW_DAY]:
            case ChannelTypeStrings[ChannelType.FLOW_TOTAL]:
            case ChannelTypeStrings[ChannelType.FLOW_EXT_COMM]:
            case ChannelTypeStrings[ChannelType.FLOW_LOGIC]:
                this.validateFlowTotal(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.EXH_GAS_DEV]:
            case ChannelTypeStrings[ChannelType.EXH_GAS_MEAN]:
                this.validateExhGas(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]:
                this.validateDigitalMotor(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.DIGITAL_CONTACT]:
            case ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM]:
            case ChannelTypeStrings[ChannelType.DIGITAL_LOGIC]:
                this.validateDigitalContact(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.ANALOG_1_5_V]:
            case ChannelTypeStrings[ChannelType.ANALOG_420MA_AI]:
            case ChannelTypeStrings[ChannelType.ANALOG_420MA_PT]:
            case ChannelTypeStrings[ChannelType.ANALOG_PT100_2WIRE]:
            case ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE]:
            case ChannelTypeStrings[ChannelType.ANALOG_TEMP_K]:
                this.validateAnalog(channelExcelObj, errors);
                break;
            case ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM]:
            case ChannelTypeStrings[ChannelType.ANALOG_LOGIC]:
                this.validateAnalogWithoutSensor(channelExcelObj, errors);
                break;
        }

        return errors.map(err => ({ ...err, rowIndex }));
    }

    private static validateCommonFields(channelExcelObj: any, errors: any[]) {
        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.FcuNo, errors);
        this.validateRequiredFieldSet(channelExcelObj, props.Tag, errors);
        this.validateRequiredFieldSet(channelExcelObj, props.Name, errors);
        this.validateRequiredFieldSet(channelExcelObj, props.ChannelType, errors);
        this.validateValueLength(channelExcelObj, props.StandardNameId, errors);
        this.validateRemarksLength(channelExcelObj, errors);
    }

    private static validateSystem(channelExcelObj: any, errors: any[]) {
        this.validateAlarmSettingsSystem(channelExcelObj, errors);
    }

    private static validateFlowTotal(channelExcelObj: any, errors: any[]) {
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsHighLevels(channelExcelObj, errors);
        this.validateFixedDigit(channelExcelObj, errors);
    }

    private static validateRunning(channelExcelObj: any, errors: any[]) {
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsHighLevels(channelExcelObj, errors);
        this.validateFixedDigit(channelExcelObj, errors);
    }

    private static validateExhGas(channelExcelObj: any, errors: any[]) {
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateMeasurementUnit(channelExcelObj, errors);
        this.validateMeasurableAndNormalRange(channelExcelObj, errors);
        this.validateFixedDigit(channelExcelObj, errors);

        const currentType = channelExcelObj[ExcelParsedProperties.ChannelType];

        if (currentType === ChannelTypeStrings[ChannelType.EXH_GAS_DEV]) {
            this.validateAlarmSettingsAllLevels(channelExcelObj, errors);
        } else if (currentType === ChannelTypeStrings[ChannelType.EXH_GAS_MEAN]) {
            if (!channelExcelObj[props.AlarmSettingLowEnable]) return;

            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.AlarmSettingLowSetPoint, errors);
            this.validateRange(channelExcelObj, props.AlarmSettingLowSetPoint, props.MeasurableRangeMax, errors, true);
            this.validateRange(channelExcelObj, props.MeasurableRangeMin, props.AlarmSettingLowSetPoint, errors, true);
        }
    }

    private static validateFixedDigit(channelExcelObj: any, errors: any[]) {
        this.validateNumericField(channelExcelObj, props.FixedDigit, errors);
    }

    private static validateDigitalMotor(channelExcelObj: any, errors: any[]) {
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsMotor(channelExcelObj, errors);
        this.validateAlarmSettingsFeedback(channelExcelObj, errors);
    }

    private static validateDigitalContact(channelExcelObj: any, errors: any[]) {
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsDigital(channelExcelObj, errors);
    }

    private static validateAnalog(channelExcelObj: any, errors: any[]) {
        this.validateMeasurementUnit(channelExcelObj, errors);
        this.validateMeasurableAndNormalRange(channelExcelObj, errors);
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsAllLevels(channelExcelObj, errors);
        this.validateAlarmSettingsSensor(channelExcelObj, errors);
        this.validateFixedDigit(channelExcelObj, errors);
    }

    private static validateAnalogWithoutSensor(channelExcelObj: any, errors: any[]) {
        this.validateMeasurementUnit(channelExcelObj, errors);
        this.validateMeasurableAndNormalRange(channelExcelObj, errors);
        this.validateInput(channelExcelObj, errors);
        this.validateOutput(channelExcelObj, errors);
        this.validateAlarmSettingsAllLevels(channelExcelObj, errors);
        this.validateFixedDigit(channelExcelObj, errors);
    }

    private static validateMeasurableAndNormalRange(channelExcelObj: any, errors: any[]) {
        const hasMeasurable = this.validateMeasurableRange(channelExcelObj, errors);
        const hasNormal = this.validateNormalRange(channelExcelObj, errors);

        const measurableMinRaw = channelExcelObj[props.MeasurableRangeMin];
        const measurableMaxRaw = channelExcelObj[props.MeasurableRangeMax];
        const normalMinRaw = channelExcelObj[props.NormalRangeMin];
        const normalMaxRaw = channelExcelObj[props.NormalRangeMax];

        const isNormalMinEmpty = !this.isValuePresent(normalMinRaw);
        const isNormalMaxEmpty = !this.isValuePresent(normalMaxRaw);

        if (isNormalMinEmpty && isNormalMaxEmpty) {
            return;
        }

        if (hasMeasurable && hasNormal && !isNormalMinEmpty && !isNormalMaxEmpty) {
            const measurableMin = Number(measurableMinRaw);
            const measurableMax = Number(measurableMaxRaw);
            const normalMin = Number(normalMinRaw);
            const normalMax = Number(normalMaxRaw);

            if (normalMin < measurableMin) {
                errors.push({
                    tag: channelExcelObj[ExcelParsedProperties.Tag],
                    id: channelExcelObj[ExcelParsedProperties.Id],
                    columnName: props.NormalRangeMin,
                    error: CHANNEL_VALIDATION.OUT_OF_RANGE,
                });
            }

            if (normalMax > measurableMax) {
                errors.push({
                    tag: channelExcelObj[ExcelParsedProperties.Tag],
                    id: channelExcelObj[ExcelParsedProperties.Id],
                    columnName: props.NormalRangeMax,
                    error: CHANNEL_VALIDATION.OUT_OF_RANGE,
                });
            }
        }
    }

    private static validateMeasurementUnit(channelExcelObj: any, errors: any[]) {
        this.validateRequiredFieldSet(channelExcelObj, props.MeasurementUnit, errors);
    }

    private static validateMeasurableRange(channelExcelObj: any, errors: any[]): boolean {
        const hasMin = this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MeasurableRangeMin, errors);
        const hasMax = this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MeasurableRangeMax, errors);
        if (hasMin && hasMax) {
            this.validateRange(channelExcelObj, props.MeasurableRangeMin, props.MeasurableRangeMax, errors);
            return true;
        }
        return false;
    }

    private static validateNormalRange(channelExcelObj: any, errors: any[]): boolean {
        const minRaw = channelExcelObj[props.NormalRangeMin];
        const maxRaw = channelExcelObj[props.NormalRangeMax];

        if (!this.isValuePresent(minRaw) && !this.isValuePresent(maxRaw)) return false;

        const hasMin = this.validateRequiredNumericFieldIsSet(channelExcelObj, props.NormalRangeMin, errors);
        const hasMax = this.validateRequiredNumericFieldIsSet(channelExcelObj, props.NormalRangeMax, errors);
        if (hasMin && hasMax) {
            this.validateRange(channelExcelObj, props.NormalRangeMin, props.NormalRangeMax, errors);
            return true;
        }
        return false;
    }

    private static validateInput(channelExcelObj: any, errors: any[]) {
        const channelType = channelExcelObj[ExcelParsedProperties.ChannelType];

        const requiresFuInputAddress = this.HARDWARE_TYPES.has(channelType) || this.RUNNING_TYPES.has(channelType);

        if (requiresFuInputAddress) {
            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataFuInputAddressFuNo, errors);

            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataFuInputAddressSlotNo, errors);

            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataFuInputAddressPin, errors);
        }

        this.validateNumericField(channelExcelObj, props.InputDataMemoryInputAddressAddress, errors);
        this.validateNumericField(channelExcelObj, props.InputDataMemoryInputAddressMask, errors);

        const isDigitalOrFlow = this.DIGITAL_TYPES.has(channelType);

        if (isDigitalOrFlow) {
            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataMiscDigitalInputFilter, errors);

            if (channelType === ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataFuInputAddressPinCount, errors);
            }
        }

        const isAnalogOrExhGas = this.ANALOG_TYPES.has(channelType);

        if (isAnalogOrExhGas) {
            this.validateRequiredNumericFieldIsSet(channelExcelObj, props.InputDataMiscHysteresis, errors);
        }
    }

    private static validateOutput(channelExcelObj: any, errors: any[]) {
        this.validateNumericField(channelExcelObj, props.OutputDataMemoryOutputAddressAddress, errors);

        this.validateNumericField(channelExcelObj, props.OutputDataMemoryOutputAddressMask, errors);

        if (channelExcelObj[ExcelParsedProperties.ChannelType] === ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
            this.validateNumericField(channelExcelObj, props.OutputDataFuOutputAddressFuNo, errors);

            this.validateNumericField(channelExcelObj, props.OutputDataFuOutputAddressSlotNo, errors);

            this.validateNumericField(channelExcelObj, props.OutputDataFuOutputAddressPin, errors);

            this.validateNumericField(channelExcelObj, props.OutputDataFuOutputAddressPinCount, errors);
            this.validateNumericField(channelExcelObj, props.OutputDataMiscSetPoint, errors);
            this.validateNumericField(channelExcelObj, props.OutputDataMiscSignalWidth, errors);
        }
    }

    private static validateAlarmSettingsDigital(channelExcelObj: any, errors: any[]) {
        if (!channelExcelObj[props.DigitalAlarmSettingEnable]) return;

        this.validateAlarmGroup(channelExcelObj, errors, {
            status: props.DigitalAlarmSettingStatusStringContactOn,
            groupNo: props.DigitalAlarmSettingAlarmGroupNo,
            repose1: props.DigitalAlarmSettingReposeGroup1No,
            repose2: props.DigitalAlarmSettingReposeGroup2No,
            delay: props.DigitalAlarmSettingDelayTimer,
            alarmLevel: props.DigitalAlarmSettingAlarmLevel,
        });

        this.validateRequiredFieldSet(channelExcelObj, props.DigitalAlarmSettingStatusStringContactOff, errors);
    }

    private static validateAlarmSettingsMotor(channelExcelObj: any, errors: any[]) {
        if (!channelExcelObj[props.MotorChannelSettingEnable]) return;

        this.validateRequiredFieldSet(channelExcelObj, props.MotorChannelSettingMotorType, errors);

        this.validateValueLength(channelExcelObj, props.MotorChannelSettingStopStatusString, errors);
        this.validateValueLength(channelExcelObj, props.MotorChannelSettingStbyStatusString, errors);

        for (let i = 1; i <= 5; i++) {
            const key = `MotorChannelSettingRunStatusString${i}` as keyof typeof props;

            this.validateValueLength(channelExcelObj, props[key], errors);
        }

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MotorChannelSettingAlarmGroupNo, errors);
        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MotorChannelSettingReposeGroup1No, errors);

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MotorChannelSettingReposeGroup2No, errors);

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.MotorChannelSettingDelayTimer, errors);

        this.validateRequiredFieldSet(channelExcelObj, props.MotorChannelSettingAlarmLevel, errors);
    }

    private static validateAlarmGroup(
        channelExcelObj: any,
        errors: any[],
        config: {
            status?: string;
            setPoint?: string;
            alarmLevel?: string;
            groupNo?: string;
            repose1?: string;
            repose2?: string;
            delay?: string;
        }
    ) {
        if (config.status) {
            this.validateRequiredFieldSet(channelExcelObj, config.status, errors);
        }

        if (config.setPoint) {
            this.validateRequiredNumericFieldIsSet(channelExcelObj, config.setPoint, errors);
        }

        const numericFields = [config.groupNo, config.repose1, config.repose2, config.delay].filter(Boolean);

        numericFields.forEach(field => this.validateRequiredNumericFieldIsSet(channelExcelObj, field, errors));

        if (config.alarmLevel) {
            this.validateRequiredFieldSet(channelExcelObj, config.alarmLevel, errors);
        }
    }

    private static validateAlarmSettingsAllLevels(channelExcelObj: any, errors: any[]) {
        const levels = [
            {
                enable: props.AlarmSettingVeryLowEnable,
                status: props.AlarmSettingVeryLowStatusString,
                setPoint: props.AlarmSettingVeryLowSetPoint,
                groupNo: props.AlarmSettingVeryLowAlarmGroupNo,
                repose1: props.AlarmSettingVeryLowReposeGroup1No,
                repose2: props.AlarmSettingVeryLowReposeGroup2No,
                delay: props.AlarmSettingVeryLowDelayTimer,
                alarmLevel: props.AlarmSettingVeryLowAlarmLevel,
            },
            {
                enable: props.AlarmSettingLowEnable,
                status: props.AlarmSettingLowStatusString,
                setPoint: props.AlarmSettingLowSetPoint,
                groupNo: props.AlarmSettingLowAlarmGroupNo,
                repose1: props.AlarmSettingLowReposeGroup1No,
                repose2: props.AlarmSettingLowReposeGroup2No,
                delay: props.AlarmSettingLowDelayTimer,
                alarmLevel: props.AlarmSettingLowAlarmLevel,
            },
            {
                enable: props.AlarmSettingHighEnable,
                status: props.AlarmSettingHighStatusString,
                setPoint: props.AlarmSettingHighSetPoint,
                groupNo: props.AlarmSettingHighAlarmGroupNo,
                repose1: props.AlarmSettingHighReposeGroup1No,
                repose2: props.AlarmSettingHighReposeGroup2No,
                delay: props.AlarmSettingHighDelayTimer,
                alarmLevel: props.AlarmSettingHighAlarmLevel,
            },
            {
                enable: props.AlarmSettingVeryHighEnable,
                status: props.AlarmSettingVeryHighStatusString,
                setPoint: props.AlarmSettingVeryHighSetPoint,
                groupNo: props.AlarmSettingVeryHighAlarmGroupNo,
                repose1: props.AlarmSettingVeryHighReposeGroup1No,
                repose2: props.AlarmSettingVeryHighReposeGroup2No,
                delay: props.AlarmSettingVeryHighDelayTimer,
                alarmLevel: props.AlarmSettingVeryHighAlarmLevel,
            },
        ];
        for (const level of levels) {
            if (!channelExcelObj[level.enable]) continue;

            this.validateAlarmGroup(channelExcelObj, errors, {
                status: level.status,
                setPoint: level.setPoint,
                groupNo: level.groupNo,
                repose1: level.repose1,
                repose2: level.repose2,
                delay: level.delay,
                alarmLevel: level.alarmLevel,
            });
        }

        this.validateAlarmHierarchy(channelExcelObj, errors);
    }

    private static validateAlarmSettingsHighLevels(channelExcelObj: any, errors: any[]) {
        const highLevels = [
            {
                enable: props.AlarmSettingHighEnable,
                status: props.AlarmSettingHighStatusString,
                setPoint: props.AlarmSettingHighSetPoint,
                groupNo: props.AlarmSettingHighAlarmGroupNo,
                repose1: props.AlarmSettingHighReposeGroup1No,
                repose2: props.AlarmSettingHighReposeGroup2No,
                delay: props.AlarmSettingHighDelayTimer,
                alarmLevel: props.AlarmSettingHighAlarmLevel,
            },
            {
                enable: props.AlarmSettingVeryHighEnable,
                status: props.AlarmSettingVeryHighStatusString,
                setPoint: props.AlarmSettingVeryHighSetPoint,
                groupNo: props.AlarmSettingVeryHighAlarmGroupNo,
                repose1: props.AlarmSettingVeryHighReposeGroup1No,
                repose2: props.AlarmSettingVeryHighReposeGroup2No,
                delay: props.AlarmSettingVeryHighDelayTimer,
                alarmLevel: props.AlarmSettingVeryHighAlarmLevel,
            },
        ];
        for (const level of highLevels) {
            if (!channelExcelObj[level.enable]) continue;

            this.validateAlarmGroup(channelExcelObj, errors, {
                status: level.status,
                setPoint: level.setPoint,
                groupNo: level.groupNo,
                repose1: level.repose1,
                repose2: level.repose2,
                delay: level.delay,
                alarmLevel: level.alarmLevel,
            });
        }

        this.validateAlarmHierarchy(channelExcelObj, errors);
    }

    private static validateAlarmSettingsSensor(channelExcelObj: any, errors: any[]) {
        if (!channelExcelObj[props.AlarmSettingSensorFailEnable]) return;

        this.validateAlarmGroup(channelExcelObj, errors, {
            status: props.AlarmSettingSensorFailStatusString,
            groupNo: props.AlarmSettingSensorFailAlarmGroupNo,
            repose1: props.AlarmSettingSensorFailReposeGroup1No,
            repose2: props.AlarmSettingSensorFailReposeGroup2No,
            delay: props.AlarmSettingSensorFailDelayTimer,
            alarmLevel: props.AlarmSettingSensorFailAlarmLevel,
        });
    }

    private static validateAlarmSettingsFeedback(channelExcelObj: any, errors: any[]) {
        if (!channelExcelObj[props.AlarmSettingFeedbackErrorEnable]) return;

        this.validateAlarmGroup(channelExcelObj, errors, {
            status: props.AlarmSettingFeedbackErrorStatusString,
            groupNo: props.AlarmSettingFeedbackErrorAlarmGroupNo,
            repose1: props.AlarmSettingFeedbackErrorReposeGroup1No,
            repose2: props.AlarmSettingFeedbackErrorReposeGroup2No,
            delay: props.AlarmSettingFeedbackErrorDelayTimer,
            alarmLevel: props.AlarmSettingFeedbackErrorAlarmLevel,
        });
    }

    private static validateAlarmSettingsSystem(channelExcelObj: any, errors: any[]) {
        if (!channelExcelObj[props.SystemAlarmSettingEnable]) return;

        for (let i = 1; i <= 16; i++) {
            const key = `SystemAlarmSettingStatusString${i}` as keyof typeof props;

            this.validateValueLength(channelExcelObj, props[key], errors);
        }

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.SystemAlarmSettingAlarmGroupNo, errors);

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.SystemAlarmSettingReposeGroup1No, errors);

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.SystemAlarmSettingReposeGroup2No, errors);

        this.validateRequiredNumericFieldIsSet(channelExcelObj, props.SystemAlarmSettingDelayTimer, errors);

        this.validateRequiredFieldSet(channelExcelObj, props.SystemAlarmSettingAlarmLevel, errors);
    }

    private static validateRange(
        excelData: any,
        expectedLowerKey: any,
        expectedHigherKey: any,
        errors: any[],
        allowEqual: boolean = false
    ) {
        const lowerRaw = excelData[expectedLowerKey];
        const higherRaw = excelData[expectedHigherKey];

        if (!this.isValuePresent(lowerRaw) || !this.isValuePresent(higherRaw)) return;

        const lower = Number(lowerRaw);
        const higher = Number(higherRaw);

        const invalid = allowEqual ? lower > higher : lower >= higher;

        if (!isNaN(lower) && !isNaN(higher) && invalid) {
            errors.push({
                tag: excelData[ExcelParsedProperties.Tag],
                id: excelData[ExcelParsedProperties.Id],
                columnNames: [expectedLowerKey, expectedHigherKey],
                error: CHANNEL_VALIDATION.RANGE,
            });
        }
    }

    private static addError(excelData: any, columnName: any, errors: any[], errorType: any): void {
        errors.push({
            tag: excelData[ExcelParsedProperties.Tag],
            id: excelData[ExcelParsedProperties.Id],
            columnName,
            error: errorType,
        });
    }

    private static validateValueLength(
        excelData: any,
        columnName: any,
        errors: any[],
        isNumber: boolean = false
    ): boolean {
        const allowedLength = CHANNEL_LENGTH_VALIDATION[columnName];
        const valueStr = excelData[columnName]?.toString();

        if (!valueStr || !allowedLength) return true;

        const lengthValue = isNumber ? valueStr.replace(/[.+-]/g, '').length : valueStr.length;

        if (lengthValue > allowedLength[1]) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.VALUE_LENGTH);
            return false;
        }
        return true;
    }

    private static validateRemarksLength(excelData: any, errors: any[]): void {
        const columnName = props.Remarks;
        const value = excelData[columnName];
        if (!this.isValuePresent(value)) return;

        const remarkParts = value.toString().split('^');
        if (remarkParts.length > 2) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.TOTAL_REMARK_LENGTH);
        }

        for (const part of remarkParts) {
            if (part.length > 15) {
                this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.REMARK_LENGTH);
                break;
            }
        }
    }

    private static validateRequiredFieldSet(excelData: any, columnName: any, errors: any[]): boolean {
        if (!this.isValuePresent(excelData[columnName])) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.REQUIRED);
            return false;
        }
        return this.validateValueLength(excelData, columnName, errors);
    }

    private static validateNumericField(excelData: any, columnName: any, errors: any[]): boolean {
        if (!this.isValuePresent(excelData[columnName])) {
            return false;
        }

        const numberValue = Number(excelData[columnName]);
        if (isNaN(numberValue)) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.NUMERIC);
            return false;
        }

        const validRange = CHANNEL_RANGE_VALIDATION[columnName];
        if (validRange && (numberValue < validRange[0] || numberValue > validRange[1])) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.VALUE_RANGE);
            return false;
        }

        return this.validateValueLength(excelData, columnName, errors, true);
    }

    private static validateRequiredNumericFieldIsSet(excelData: any, columnName: any, errors: any[]): boolean {
        if (!this.isValuePresent(excelData[columnName])) {
            this.addError(excelData, columnName, errors, CHANNEL_VALIDATION.REQUIRED);
            return false;
        }
        return this.validateNumericField(excelData, columnName, errors);
    }

    private static validateAlarmHierarchy(channelExcelObj: any, errors: any[]) {
        const id = channelExcelObj[ExcelParsedProperties.Id];

        const addRangeComparisonError = (columnNames: string[], messageNames, low: boolean = false) => {
            errors.push({
                tag: channelExcelObj[ExcelParsedProperties.Tag],
                id,
                columnNames,
                messageNames,
                low,
                error: CHANNEL_VALIDATION.RANGE_COMPARISON,
            });
        };

        const addOutOfRangeError = (columnName, alarmType) => {
            errors.push({
                tag: channelExcelObj[ExcelParsedProperties.Tag],
                id,
                columnName,
                alarmType,
                error: CHANNEL_VALIDATION.OUT_OF_RANGE,
            });
        };

        const min = this.parseNumber(channelExcelObj[props.MeasurableRangeMin]);
        const max = this.parseNumber(channelExcelObj[props.MeasurableRangeMax]);

        if (min === null || max === null || min >= max) {
            return;
        }

        const alarms = {
            lowLow: channelExcelObj[props.AlarmSettingVeryLowEnable]
                ? this.parseNumber(channelExcelObj[props.AlarmSettingVeryLowSetPoint])
                : null,

            low: channelExcelObj[props.AlarmSettingLowEnable]
                ? this.parseNumber(channelExcelObj[props.AlarmSettingLowSetPoint])
                : null,

            high: channelExcelObj[props.AlarmSettingHighEnable]
                ? this.parseNumber(channelExcelObj[props.AlarmSettingHighSetPoint])
                : null,

            veryHigh: channelExcelObj[props.AlarmSettingVeryHighEnable]
                ? this.parseNumber(channelExcelObj[props.AlarmSettingVeryHighSetPoint])
                : null,
        };

        const measurableChecks = [
            {
                value: alarms.lowLow,
                column: props.AlarmSettingVeryLowSetPoint,
                alarmType: 'LL',
            },
            {
                value: alarms.low,
                column: props.AlarmSettingLowSetPoint,
                alarmType: 'L',
            },
            {
                value: alarms.high,
                column: props.AlarmSettingHighSetPoint,
                alarmType: 'H',
            },
            {
                value: alarms.veryHigh,
                column: props.AlarmSettingVeryHighSetPoint,
                alarmType: 'HH',
            },
        ];

        measurableChecks.forEach(check => {
            if (check.value !== null && (check.value < min || check.value > max)) {
                addOutOfRangeError(check.column, check.alarmType);
            }
        });

        const hierarchyChecks = [
            {
                invalid: alarms.lowLow !== null && alarms.low !== null && alarms.lowLow >= alarms.low,
                columns: [props.AlarmSettingVeryLowSetPoint, props.AlarmSettingLowSetPoint],
                messageNames: [msgprops.AlarmSettingLowSetPoint, msgprops.AlarmSettingVeryLowSetPoint],
            },
            {
                invalid: alarms.low !== null && alarms.high !== null && alarms.low >= alarms.high,
                columns: [props.AlarmSettingLowSetPoint, props.AlarmSettingHighSetPoint],
                messageNames: [msgprops.AlarmSettingHighSetPoint, msgprops.AlarmSettingLowSetPoint],
            },
            {
                invalid: alarms.high !== null && alarms.veryHigh !== null && alarms.high >= alarms.veryHigh,
                columns: [props.AlarmSettingHighSetPoint, props.AlarmSettingVeryHighSetPoint],
                messageNames: [msgprops.AlarmSettingVeryHighSetPoint, msgprops.AlarmSettingHighSetPoint],
            },
        ];

        hierarchyChecks.forEach(check => {
            if (check.invalid) {
                addRangeComparisonError(check.columns, check.messageNames);
            }
        });

        const normalMin = this.parseNumber(channelExcelObj[props.NormalRangeMin]);
        const normalMax = this.parseNumber(channelExcelObj[props.NormalRangeMax]);

        const hasValidNormalRange =
            normalMin !== null && normalMax !== null && normalMin < normalMax && normalMin > min && normalMax < max;

        if (hasValidNormalRange) {
            const normalChecks = [
                {
                    invalid: alarms.lowLow !== null && alarms.lowLow >= normalMin,
                    columns: [props.NormalRangeMin, props.AlarmSettingVeryLowSetPoint],
                    messageNames: [props.NormalRangeMin, msgprops.AlarmSettingVeryLowSetPoint],
                    low: true,
                },
                {
                    invalid: alarms.low !== null && alarms.low >= normalMin,
                    columns: [props.NormalRangeMin, props.AlarmSettingLowSetPoint],
                    messageNames: [props.NormalRangeMin, msgprops.AlarmSettingLowSetPoint],
                    low: true,
                },
                {
                    invalid: alarms.high !== null && alarms.high <= normalMax,
                    columns: [props.NormalRangeMax, props.AlarmSettingHighSetPoint],
                    messageNames: [msgprops.AlarmSettingHighSetPoint, props.NormalRangeMax],
                },
                {
                    invalid: alarms.veryHigh !== null && alarms.veryHigh <= normalMax,
                    columns: [props.NormalRangeMax, props.AlarmSettingVeryHighSetPoint],
                    messageNames: [msgprops.AlarmSettingVeryHighSetPoint, props.NormalRangeMax],
                },
            ];

            normalChecks.forEach(check => {
                if (check.invalid) {
                    addRangeComparisonError(check.columns, check.messageNames, check.low);
                }
            });
        }
    }
}
